import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import CariCard from '../components/CariCard';
import DataTable from '../components/DataTable';
import { fmtAmount } from '../utils/numberFormat';
import { Button, Modal, FormField, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardSplitButton } from '../components/card';
import { TAB_ICONS } from '../utils/tabIcons';

const STATUS_LABELS = { issued: 'Verildi', used: 'Kullanıldı', cancelled: 'İptal', expired: 'Süresi Dolmuş' };
const STATUS_COLORS = { issued: '#3b82f6', used: '#16a34a', cancelled: '#64748b', expired: '#dc2626' };

const cariLabel = (a) => `${a.code || ''} - ${a.title || ''}`;
const cariSub = (a) => {
  const p = [];
  if (a.phone || a.mobile) p.push(a.phone || a.mobile);
  if (a.identity_type === 'individual') { if (a.id_number) p.push('TC: ' + a.id_number); }
  else { if (a.tax_office) p.push(a.tax_office); if (a.tax_number) p.push(a.tax_number); }
  return p.filter(Boolean).join(' | ');
};

function BalancePill({ account }) {
  const bal = parseFloat(account?.balance) || 0;
  const cur = account?.currency_code || 'TRY';
  const isDebit = bal > 0, isCredit = bal < 0;
  const color = isDebit ? '#dc2626' : isCredit ? '#16a34a' : '#64748b';
  const bg    = isDebit ? 'rgba(220,38,38,0.10)' : isCredit ? 'rgba(22,163,74,0.10)' : 'rgba(100,116,139,0.08)';
  const label = isDebit ? 'Cari bize borçlu' : isCredit ? 'Cari bizden alacaklı' : 'Bakiye sıfır';
  const dir   = isDebit ? '🔴 BORÇ' : isCredit ? '🟢 ALACAK' : '⚪ KAPALI';
  return (
    <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: bg,
                  border: `1px solid ${color}33`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12 }}>
      <span style={{ fontWeight: 700, color, letterSpacing: '0.04em' }}>{dir}</span>
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontWeight: 700, color, fontSize: 14 }}>
        {fmtAmount(Math.abs(bal))} {cur}
      </span>
    </div>
  );
}

const emptyForm = {
  account_id: '', issued_to_name: '',
  amount: '', currency_code: 'TRY',
  issue_date: new Date().toISOString().slice(0, 10),
  expiry_date: '',
  notes: '',
};

function ShoppingVouchersPage({ onNavigate = null }) {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', status: '', account_id: '' });

  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [isNew, setIsNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showAccountList, setShowAccountList] = useState(false);
  const [accountListSearch, setAccountListSearch] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get('/shopping-vouchers', { params });
      if (r.data.success) setItems(r.data.data.items);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filters]);

  const fetchAccounts = useCallback(async () => {
    try {
      const r = await api.get('/accounts', { params: { limit: 500 } });
      if (r.data.success) setAccounts((r.data.data.items || []).filter(a => a.is_active));
    } catch {}
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const openNew = () => {
    setSelected(null); setForm({ ...emptyForm });
    setIsNew(true); setIsEditing(true); setMsg(null); setShowModal(true);
  };
  const openDetail = (v) => {
    setSelected(v);
    setForm({
      account_id: v.account_id || '', issued_to_name: v.issued_to_name || '',
      amount: v.amount, currency_code: v.currency_code || 'TRY',
      issue_date: v.issue_date?.slice(0, 10) || '',
      expiry_date: v.expiry_date?.slice(0, 10) || '',
      notes: v.notes || '',
    });
    setIsNew(false); setIsEditing(false); setMsg(null); setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setSelected(null); setMsg(null); };

  const f = (k, val) => setForm(prev => ({ ...prev, [k]: val }));

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.amount || parseFloat(form.amount) <= 0) { setMsg({ type: 'error', text: 'Tutar girin' }); return; }
    if (!form.issue_date) { setMsg({ type: 'error', text: 'Verilme tarihi girin' }); return; }
    setSaving(true); setMsg(null);
    try {
      if (isNew) await api.post('/shopping-vouchers', form);
      else await api.put('/shopping-vouchers/' + selected.id, form);
      closeModal(); fetchList();
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi' }); }
    finally { setSaving(false); }
  };

  const handleCancel = async () => {
    if (!selected || !window.confirm('Bu çeki iptal etmek istiyor musunuz?')) return;
    try {
      await api.patch('/shopping-vouchers/' + selected.id + '/cancel');
      closeModal(); fetchList();
    } catch (err) { alert(err.response?.data?.message || 'İptal edilemedi'); }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm('Silmek istiyor musunuz?')) return;
    try {
      await api.delete('/shopping-vouchers/' + selected.id);
      closeModal(); fetchList();
    } catch (err) { alert(err.response?.data?.message || 'Silinemedi'); }
  };

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  // Görüntüleme modunda extra "İptal Et" aksiyonu body üstünde gösterilir.
  const inEdit = isEditing;
  const inView = !isEditing && !isNew;
  const showCancelAction = inView && selected?.status === 'issued';

  const modalTitle = isNew ? 'Yeni Alış Veriş Çeki' : (selected?.voucher_no || 'Çek');

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Alış Veriş Çekleri"
        count={items.length}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Çek no, cari..."
        onRefresh={fetchList}
        refreshing={loading}
        /* Manuel çek ekleme kapalı — çekler sadece tahsilat/tediye/perakende akışlarıyla üretilir, listede sadece detay görme. */
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'date_from', type: 'date', label: 'İlk Tarih' },
          { key: 'date_to', type: 'date', label: 'Son Tarih' },
          { key: 'status', type: 'select', label: 'Durum', options: [
            { value: '', label: 'Tüm Durumlar' },
            ...Object.entries(STATUS_LABELS).map(([k, v]) => ({ value: k, label: v })),
          ] },
          { key: 'account_id', type: 'select', label: 'Cari', flex: '1 1 240px', minWidth: 200, options: [
            { value: '', label: 'Tüm Cariler' },
            ...accounts.map(a => ({ value: a.id, label: `${a.code || ''} - ${a.title || ''}`.trim() })),
          ] },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="shopping-vouchers-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Kayıt yok"
          data={items}
          rowKey="id"
          onRowClick={v => openDetail(v)}
          columns={[
            { id: 'voucher_no', header: 'Çek No', accessor: 'voucher_no', sortable: true, width: 130,
              cell: v => <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent-primary)' }}>{v.voucher_no}</span> },
            { id: 'account', header: 'Verilen Cari', accessor: v => v.account_title || v.issued_to_name || '', sortable: true,
              cell: v => <strong>{v.account_title || v.issued_to_name || '—'}</strong> },
            { id: 'issue_date', header: 'Verilme Tarihi', accessor: 'issue_date', sortable: true, width: 130,
              sortFn: (a,b) => new Date(a.issue_date||0)-new Date(b.issue_date||0),
              cell: v => v.issue_date ? new Date(v.issue_date).toLocaleDateString('tr-TR') : '—' },
            { id: 'expiry_date', header: 'Son Kullanım', accessor: 'expiry_date', sortable: true, width: 130,
              sortFn: (a,b) => new Date(a.expiry_date||0)-new Date(b.expiry_date||0),
              cell: v => <span className="stock-secondary">{v.expiry_date ? new Date(v.expiry_date).toLocaleDateString('tr-TR') : '—'}</span> },
            { id: 'amount', header: 'Tutar', accessor: v => parseFloat(v.amount)||0, sortable: true, width: 150,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 },
              cell: v => `${fmtAmount(v.amount)} ${v.currency_code}` },
            { id: 'status', header: 'Durum', accessor: v => STATUS_LABELS[v.status] || v.status, sortable: true, width: 110,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: v => { const color = STATUS_COLORS[v.status] || '#64748b'; return <span style={{ background: color + '22', color, padding: '3px 9px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 }}>{STATUS_LABELS[v.status] || v.status}</span>; } },
          ]}
        />
      </div>

      {showModal && (
        <Modal
          icon={null}
          title={modalTitle}
          size="md"
          onClose={closeModal}
          footer={
            <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Button variant="ghost" onClick={closeModal}>Kapat</Button>
            </div>
          }
        >
          {/* Kaynak banner — tediyeden üretilmişse */}
          {!isNew && selected?.source_type && (
            <div style={{
              margin: '0 0 14px', padding: '10px 14px',
              background: 'rgba(124,58,237,0.08)',
              border: '1px solid rgba(124,58,237,0.25)',
              borderRadius: 8, fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'
            }}>
              <span style={{ fontSize: 18 }}>🔗</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#7c3aed' }}>
                  {selected.source_type === 'cari_payment' ? 'Tediye Makbuzundan' : selected.source_type}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  Bu alışveriş çeki <strong>{selected.source_payment_no || ''}</strong> nolu tediye makbuzundan kesildi.
                  Düzenleme/silme makbuz üzerinden yapılır.
                </div>
              </div>
              {onNavigate && selected.source_id && (
                <Button size="sm" variant="ghost"
                        onClick={() => { closeModal(); onNavigate('cari-tediye', selected.source_id); }}>
                  Makbuzu Aç →
                </Button>
              )}
            </div>
          )}

          {!isNew && selected && (
            <CardHero
              icon="🎫"
              code={selected.voucher_no}
              title="Alışveriş Çeki"
              subtitle={selected.account_title || (selected.issue_date && new Date(selected.issue_date).toLocaleDateString('tr-TR'))}
              statusBadge={
                selected.status
                  ? <CardStatusBadge
                      label={STATUS_LABELS[selected.status] || selected.status}
                      color={STATUS_COLORS[selected.status] || '#64748b'}
                      bg={(STATUS_COLORS[selected.status] || '#64748b') + '22'}
                    />
                  : null
              }
              kpis={[
                selected.amount != null && {
                  label: 'Tutar', value: fmtAmount(selected.amount),
                  color: 'primary', sublabel: selected.currency_code || 'TRY',
                },
                selected.expiry_date && {
                  label: 'Geçerlilik', value: new Date(selected.expiry_date).toLocaleDateString('tr-TR'), color: 'warning',
                },
              ].filter(Boolean)}
            />
          )}
          {!isNew && selected && (
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ background: (STATUS_COLORS[selected.status] || '#64748b') + '22', color: STATUS_COLORS[selected.status] || '#64748b', padding: '4px 10px', borderRadius: 4, fontSize: '0.78rem', fontWeight: 600 }}>
                {STATUS_LABELS[selected.status]}
              </span>
              {showCancelAction && (
                <Button variant="ghost" size="sm" onClick={handleCancel}>İptal Et</Button>
              )}
            </div>
          )}

          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            {/* CARI SEÇİMİ — tam genişlik */}
            <div style={{ gridColumn: '1 / -1' }}>
              <FormField label="Verilen Cari">
                {(() => {
                  const selCari = (accounts || []).find(a => String(a.id) === String(form.account_id));
                  return (
                    <>
                      <CariCard
                        cari={selCari}
                        disabled={!isEditing}
                        label="Verilen Cari (opsiyonel)"
                        onPick={() => { setAccountListSearch(''); setShowAccountList(true); }}
                      />
                      {selCari && <BalancePill account={selCari} />}
                      {isEditing && form.account_id && (
                        <button type="button" onClick={() => f('account_id', '')}
                                style={{ marginTop: 6, fontSize: 12, padding: '4px 10px', borderRadius: 6,
                                         border: '1px solid var(--border-secondary)', background: 'transparent',
                                         color: 'var(--text-tertiary)', cursor: 'pointer' }}>
                          ✕ Cari seçimini temizle
                        </button>
                      )}
                    </>
                  );
                })()}
              </FormField>
            </div>
            <FormField label="Adına Verilen">
              <input value={form.issued_to_name} onChange={e => f('issued_to_name', e.target.value)} disabled={!isEditing} placeholder="Cari yoksa manuel" />
            </FormField>
            <FormField label="Tutar" required>
              <input type="number" step="0.01" value={form.amount} onChange={e => f('amount', e.target.value)} disabled={!isEditing} />
            </FormField>
            <FormField label="Para Birimi">
              <select value={form.currency_code} onChange={e => f('currency_code', e.target.value)} disabled={!isEditing}>
                <option>TRY</option><option>USD</option><option>EUR</option><option>GBP</option>
              </select>
            </FormField>
            <FormField label="Verilme Tarihi" required>
              <input type="date" value={form.issue_date} onChange={e => f('issue_date', e.target.value)} disabled={!isEditing} />
            </FormField>
            <FormField label="Son Kullanım">
              <input type="date" value={form.expiry_date} onChange={e => f('expiry_date', e.target.value)} disabled={!isEditing} />
            </FormField>
            <FormField label="Notlar" fullWidth>
              <textarea rows="3" value={form.notes} onChange={e => f('notes', e.target.value)} disabled={!isEditing} />
            </FormField>
          </div>

          {!isNew && selected?.status === 'used' && (
            <div style={{ marginTop: 12, background: 'var(--bg-secondary)', padding: 10, borderRadius: 6, fontSize: '0.85rem' }}>
              Bu çek <strong>{selected.used_date ? new Date(selected.used_date).toLocaleDateString('tr-TR') : ''}</strong> tarihinde kullanılmış.
            </div>
          )}
        </Modal>
      )}

      {/* CARI LİSTESİ MODALI */}
      {showAccountList && (() => {
        const q = (accountListSearch || '').toLowerCase().trim();
        const filtered = (accounts || []).filter(a => !q ||
          (a.code || '').toLowerCase().includes(q) ||
          (a.title || '').toLowerCase().includes(q) ||
          (a.tax_number || '').includes(q) ||
          (a.id_number || '').includes(q) ||
          (a.phone || '').includes(q) ||
          (a.mobile || '').includes(q));
        const cariCols = [
          { id: 'code', header: 'Kod', accessor: 'code', sortable: true, width: 110,
            cell: a => <span className="stock-code">{a.code}</span> },
          { id: 'title', header: 'Ünvan', accessor: 'title', sortable: true,
            cell: a => <span className="stock-name-cell">{a.title}</span> },
          { id: 'type', header: 'Tip', accessor: 'type', sortable: true, width: 100 },
          { id: 'tax', header: 'VD / TC', sortable: true, width: 150,
            accessor: a => a.identity_type === 'individual' ? (a.id_number || '') : `${a.tax_office || ''} ${a.tax_number || ''}`,
            cell: a => <span className="stock-secondary" style={{ fontSize: '0.78rem' }}>
              {a.identity_type === 'individual' ? (a.id_number || '—') : (a.tax_office || a.tax_number ? `${a.tax_office || ''}${a.tax_office && a.tax_number ? ' / ' : ''}${a.tax_number || ''}` : '—')}
            </span> },
          { id: 'phone', header: 'Telefon', sortable: true, width: 130,
            accessor: a => a.phone || a.mobile || '',
            cell: a => <span className="stock-secondary">{a.phone || a.mobile || '—'}</span> },
          { id: 'balance', header: 'Bakiye', accessor: a => parseFloat(a.balance) || 0, sortable: true, width: 170,
            headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
            cell: a => {
              const b = parseFloat(a.balance) || 0;
              const color = b > 0 ? 'var(--danger)' : b < 0 ? 'var(--success)' : 'var(--text-tertiary)';
              const sfx = b > 0 ? ' B' : b < 0 ? ' A' : '';
              return <span style={{ fontFamily: 'monospace', color, fontWeight: 600 }}>
                {fmtAmount(Math.abs(b))} {a.currency_code || 'TRY'}{sfx}
              </span>;
            } },
        ];
        return (
          <div className="modal-overlay" onClick={() => setShowAccountList(false)}>
            <div className="modal-box" style={{ maxWidth: 980, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: 0 }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-secondary)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Cari Listesi</h3>
                <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 12, background: 'var(--accent-glow)', color: 'var(--accent-primary)', fontWeight: 600 }}>{filtered.length} kayıt</span>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <input autoFocus type="text" placeholder="Kod, ünvan, VD/TC, telefon ile ara..."
                    value={accountListSearch} onChange={e => setAccountListSearch(e.target.value)}
                    style={{ width: '100%', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>
                <button onClick={() => setShowAccountList(false)} title="Kapat"
                  style={{ width: 32, height: 32, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border-secondary)', borderRadius: 8, background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  ✕
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'auto' }} className="stocks-table-wrap">
                <DataTable
                  pageKey="shopping-voucher-cari-picker"
                  tableClassName="stocks-table"
                  rowClassName="stocks-table-row"
                  emptyText={q ? 'Eşleşen cari bulunamadı' : 'Henüz cari yok'}
                  data={filtered}
                  rowKey="id"
                  onRowClick={a => { f('account_id', a.id); setShowAccountList(false); }}
                  columns={cariCols}
                />
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default ShoppingVouchersPage;
